/**
 * openable-refs, browser side: the RefHost the viewer's RefStore talks to. Requests go to the
 * run's machine as signed, encrypted commands (`run.resolve_refs`, `run.read_ref`); replies are
 * decrypted by the backend and validated here. Bodies over the inline cap arrive as encrypted
 * artifact references, read with this account's artifact reader — never a hosted URL.
 */
import {
  type EncryptedArtifactRef,
  ReadRefResult,
  type RefBody,
  ResolveRefsResult,
} from '@viko/protocol'
import { type OpenedRef, type RefData, type RefHost, RefStore } from '@viko/transcript-ui'
import { useMemo, useRef } from 'react'
import type { Backend } from './backend.js'

type Dispatch = Pick<Backend, 'dispatch'>
interface Reader {
  read(ref: EncryptedArtifactRef): Promise<{ bytes: Uint8Array }>
}

function base64Bytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function createRefHost(o: {
  backend: Dispatch
  machineId: string
  runId: string
  /** Account from the trusted session; artifact refs for any other account/run are refused. */
  accountId: string
  reader?: Reader | undefined
}): RefHost {
  const decode = async (body: RefBody | undefined, text: boolean): Promise<RefData | undefined> => {
    if (!body) return undefined
    if (body.encoding === 'utf8') return { text: body.text }
    let bytes: Uint8Array
    if (body.encoding === 'base64') bytes = base64Bytes(body.data)
    else {
      const ref = body.artifact
      if (!o.reader) throw new Error('Unlock encrypted content on this device to open large files.')
      if (ref.accountId !== o.accountId || ref.runId !== o.runId)
        throw new Error('The machine returned a file for a different account or run.')
      bytes = (await o.reader.read(ref)).bytes
    }
    return text ? { text: new TextDecoder().decode(bytes) } : { bytes }
  }
  return {
    async resolve(refs) {
      const out = await o.backend.dispatch('run.resolve_refs', o.machineId, {
        runId: o.runId,
        refs,
      })
      return ResolveRefsResult.parse(out.result).results
    },
    async read(target): Promise<OpenedRef> {
      const out = await o.backend.dispatch('run.read_ref', o.machineId, { runId: o.runId, target })
      const r = ReadRefResult.parse(out.result)
      if (r.type === 'preview') {
        const { body, ...rest } = r
        const data = await decode(body, true)
        return { ...rest, html: data && 'text' in data ? data.text : '' }
      }
      if (r.type !== 'file') return r
      const { body, preview, ...rest } = r
      const [data, previewData] = await Promise.all([decode(body, r.text), decode(preview, false)])
      return { ...rest, ...(data ? { data } : {}), ...(previewData ? { previewData } : {}) }
    },
  }
}

/** One RefStore per run view (answers are cached for the life of the view). */
export function useRefStore(
  b: Backend,
  o: {
    runId: string
    machineId: string
    enabled: boolean
    hasArtifact?: (id: string) => boolean
  },
): RefStore | undefined {
  const accountId = b.artifactAccess.accountId
  const reader = b.artifacts
  const has = useRef(o.hasArtifact)
  has.current = o.hasArtifact
  return useMemo(
    () =>
      o.enabled && o.machineId
        ? new RefStore(
            createRefHost({
              backend: b,
              machineId: o.machineId,
              runId: o.runId,
              accountId,
              reader,
            }),
            { hasArtifact: (id) => has.current?.(id) ?? false },
          )
        : undefined,
    [b, o.runId, o.machineId, o.enabled, accountId, reader],
  )
}
